/**
 * Context-free navigation progress bar.
 *
 * The SRP/job/best-companies-dimension/locations/salary/salary-role "island" routes deliberately
 * never mount `LayoutDefault` (progressive-takeover architecture — each mounts its own isolated
 * provider tree straight into an empty container instead, see `pages/+onRenderClient.ts`). Since
 * `NavigationProgress` reads `useNavigation()` from `NavigationContext`, which is only provided
 * inside `LayoutDefault`, it silently never renders on any of those routes — so a click that
 * triggers a slow client-side navigation (e.g. an SRP job card, or a map popup job link) shows no
 * visual feedback at all until the destination page suddenly appears.
 *
 * Vike's own `vike:pageTransitionStart`/`vike:pageTransitionEnd` events (dispatched globally by
 * `+onPageTransitionStart.ts`/`+onPageTransitionEnd.ts`, already gated to skip the initial SSR
 * load) fire on `window` regardless of which — if any — React tree is mounted. A plain DOM
 * listener can therefore show progress everywhere the React-context version can't, without
 * touching the fragile island provider-scoping described in the knowledge graph.
 */

const STUCK_TIMEOUT_MS = 15_000;

let initialized = false;

export function initStandaloneNavigationProgress(): void {
  if (typeof window === 'undefined' || initialized) return;
  initialized = true;

  const bar = document.createElement('div');
  bar.setAttribute('aria-hidden', 'true');
  bar.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'height:3px',
    'width:0%',
    'z-index:2147483000',
    'pointer-events:none',
    'background:linear-gradient(to right, #22c55e, #3b82f6, #22c55e)',
    'box-shadow:0 0 15px rgba(34,197,94,0.6), 0 0 30px rgba(34,197,94,0.3)',
    'transition:width 0.05s linear, opacity 0.2s ease-out',
    'opacity:0',
  ].join(';');

  let progress = 0;
  let tickHandle: ReturnType<typeof setInterval> | null = null;
  let hideTimeout: ReturnType<typeof setTimeout> | null = null;
  let stuckTimeout: ReturnType<typeof setTimeout> | null = null;

  const clearTimers = () => {
    if (tickHandle !== null) {
      clearInterval(tickHandle);
      tickHandle = null;
    }
    if (hideTimeout !== null) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
    if (stuckTimeout !== null) {
      clearTimeout(stuckTimeout);
      stuckTimeout = null;
    }
  };

  const finish = (): void => {
    clearTimers();
    progress = 100;
    bar.style.width = '100%';
    hideTimeout = setTimeout(() => {
      bar.style.opacity = '0';
      hideTimeout = setTimeout(() => {
        bar.style.width = '0%';
      }, 200);
    }, 150);
  };

  const start = (): void => {
    if (!bar.isConnected) document.body.appendChild(bar);
    clearTimers();
    progress = 0;
    bar.style.opacity = '1';
    bar.style.width = '0%';
    tickHandle = setInterval(() => {
      // Same exponential-easing curve as NavigationProgress.tsx: fast start, slow approach to 90%.
      progress = Math.min(progress + (100 - progress) * 0.1, 90);
      bar.style.width = `${progress}%`;
    }, 50);
    // If vike:pageTransitionEnd never fires (a hung/errored client navigation), don't leave the
    // bar stuck mid-way forever — matches NavigationContext's own NAVIGATION_STUCK_TIMEOUT_MS.
    stuckTimeout = setTimeout(finish, STUCK_TIMEOUT_MS);
  };

  window.addEventListener('vike:pageTransitionStart', start);
  window.addEventListener('vike:pageTransitionEnd', finish);
}
